import type { TrafficRow } from '@netfoundry-ui/shared/model';
import type {
    SankeyChartLink,
    SankeyChartNode,
    SankeyLinkClickEvent,
    SankeyNodeClickEvent,
} from './flow-map-chart.component';

/**
 * Turns parsed traffic flows into the flow-map (Sankey) chart's generic nodes/links.
 *
 * Four columns, following the real traffic path end to end: source address -> source identity ->
 * host identity -> endpoint (address:port). The device's address (where the traffic starts) comes first,
 * then the source identity (the dialer - the gateway/edge router that captures the traffic and forwards it
 * onward), then the host identity (the binder - the identity that hosts the service on the far side), then
 * the specific address:port a service would govern. Routing the traffic through both identities this way
 * replaces the many-to-many source-to-destination tangle with cleaner fans, and the usual shape is many
 * source identities fanning into a few host identities. Ribbon thickness = summed circuit count (the only
 * magnitude the data carries today). Node ids are stage-qualified (`<stage>:<value>`) so a value never
 * collides across columns.
 *
 * Four data facts shape the grouping:
 *  - Ziti identity initiators have no remote IP (the parser sets it to "(empty)"), so they get a shared
 *    "(no source address)" placeholder node in the first column so the ribbon stays continuous.
 *  - A flow with no source identity name gets a "(no identity)" placeholder node in the source-identity
 *    column; a flow with no host identity name gets a "(no host identity)" placeholder in the host-identity
 *    column.
 *  - Real networks have far more addresses than a chart can show, so each column keeps its top entries by
 *    circuits and folds the rest into its own "Other" catch-all (folded source addresses into "Other
 *    sources"; the endpoints of folded destination addresses into "Other destinations").
 *
 * The build also indexes which flows sit under each node and ribbon, so a click resolves to its
 * underlying flows without re-deriving the keys.
 */

const EMPTY = '(empty)'; // matches traffic-flow.util's placeholder for a missing initiator IP
const OTHER = '__other__'; // folded-overflow bucket, per column
const NO_PORT = '(no port)'; // bucket for flows the template left without a terminator port
const NO_SRC_ADDR = '__no_src_addr__'; // placeholder source-address node for no-IP (identity) initiators
const NO_IDENTITY = '__no_identity__'; // placeholder node for flows with no source identity name
const NO_HOST_IDENTITY = '__no_host_identity__'; // placeholder node for flows with no host identity name

/** How many real entries each high-cardinality column shows before the rest fold into "Other". */
const MAX_IDENTITIES = 40;
const MAX_HOST_IDENTITIES = 40;
const MAX_ADDRESSES = 40;
const MAX_SRC_ADDRESSES = 30;

/** Stage indexes, named so the adapter and resolvers read clearly. The source address (the device that
 *  originates the traffic) comes first; the source identity (the dialer that captures it) second; the host
 *  identity (the binder that hosts the service) third. */
const STAGE_SRC_ADDR = 0;
const STAGE_IDENTITY = 1;
const STAGE_HOST_IDENTITY = 2;
/** The final column: one node per specific endpoint (address:port) - the thing a service governs. */
const STAGE_ENDPOINT = 3;

export const FLOW_MAP_HEADERS = ['Source address', 'Source identity', 'Host identity', 'Host address'];

export interface TrafficSankeyData {
    nodes: SankeyChartNode[];
    links: SankeyChartLink[];
    headers: string[];
    /** Flows under each node id, for click-through. */
    flowsByNode: Map<string, TrafficRow[]>;
    /** Flows under each ribbon, keyed by the link id (`<sourceId>-><targetId>`). */
    flowsByLink: Map<string, TrafficRow[]>;
    /** Node label by id, so a click can title its detail panel. */
    labelById: Map<string, string>;
}

/**
 * Drill-down options. The source-address column is collapsed by default (the chart starts at source
 * identity, so the many-addresses "wall" is hidden); passing a source identity's key in `expandedIdentities`
 * reveals just that identity's source addresses as a ragged column to its left. Each identity node carries
 * an expand pill (its distinct source-address count) so a click can toggle it in/out of this set. The key is
 * the identity's raw node value: the part of its node id after the stage prefix (e.g. `1:scada-gw` -> `scada-gw`).
 */
export interface TrafficSankeyOptions {
    expandedIdentities?: ReadonlySet<string>;
}

/** How the detail panel breaks the selected flows into rows - the dimension that varies usefully. */
export type FlowBreakdown =
    | 'flow'
    | 'endpoint'
    | 'address'
    | 'port'
    | 'source'
    | 'identity'
    | 'host-source'
    | 'source-address'
    | 'service';

/** A figure the detail panel can show for a selection; the panel computes each from the flows. */
export type FlowMetric =
    | 'circuits'
    | 'sources'
    | 'sourceAddresses'
    | 'sourceIdentities'
    | 'destinations'
    | 'ports'
    | 'services'
    | 'port';

/** What a click resolves to: a titled panel, the flows behind it, and how to summarize them. */
export interface ResolvedFlows {
    eyebrow: string;
    title: string;
    /** The thing a created service would target (an address, or an address:port), for the panel's lede. */
    subject: string;
    flows: TrafficRow[];
    /** How the panel groups the flows into rows. */
    breakdown: FlowBreakdown;
    /** The summary figures to show, in order. */
    metrics: FlowMetric[];
    /** When set, the panel offers these breakdowns as group-by tiles (the first is the initial grouping,
     *  which `breakdown` also names), and the operator can switch among them - one table, pivoted - rather
     *  than seeing a fixed breakdown plus a separate services list. */
    groupBy?: FlowBreakdown[];
    /** Whether this kind of selection can seed a service (a concrete address or address:port). */
    canCreate: boolean;
    /** Render the title in monospace - set when it is an address or endpoint rather than an entity name. */
    mono?: boolean;
    /** A muted, display-only suffix after the title (e.g. ":3389"), so an endpoint reads as address + port. */
    titleTail?: string;
}

function addressOf(flow: TrafficRow): string {
    return (flow.terminator_remote_address ?? '').trim();
}

function portOf(flow: TrafficRow): string {
    return (flow.terminator_remote_port ?? '').trim();
}

/** The stage keys a flow maps to (before any overflow folding). `address` is the destination address. */
interface FlowKeys {
    identity: string;
    hostIdentity: string;
    srcAddr: string;
    address: string;
    port: string;
}

function keysOf(flow: TrafficRow): FlowKeys {
    const ip = flow.initiator_remote_ip;
    const hasIp = !!ip && ip !== EMPTY;
    const identity = (flow.identity_name ?? '').trim() || NO_IDENTITY;
    const hostIdentity = (flow.host_entity_name ?? '').trim() || NO_HOST_IDENTITY;
    const srcAddr = hasIp ? ip : NO_SRC_ADDR;
    return { identity, hostIdentity, srcAddr, address: addressOf(flow), port: portOf(flow) || NO_PORT };
}

function sumCircuits(flows: TrafficRow[]): number {
    return flows.reduce((total, flow) => total + (flow.circuits || 0), 0);
}

/** The distinct, non-blank service names among a set of flows. */
function distinctServiceNames(flows: TrafficRow[]): string[] {
    const services = new Set<string>();
    flows.forEach((flow) => {
        const service = (flow.service_name ?? '').trim();
        if (service) {
            services.add(service);
        }
    });
    return [...services];
}

/** The service a ribbon carries: the single service name, or "N services" when it spans several; undefined
 *  when none is known. Used for the aggregate on-ribbon label on the source-to-host leg. */
function linkServiceLabel(flows: TrafficRow[]): string | undefined {
    const services = distinctServiceNames(flows);
    if (services.length === 0) {
        return undefined;
    }
    return services.length === 1 ? services[0] : `${services.length} services`;
}

/** The service(s) at a concrete endpoint: the actual name(s) when only a couple serve it (so an endpoint
 *  reached by, say, a specific service and a catch-all names both), or "N services" once there are too many
 *  to read. Shown once under the endpoint's address, where the aggregate ribbon count does not help. */
function nodeServiceLabel(flows: TrafficRow[]): string | undefined {
    const services = distinctServiceNames(flows);
    if (services.length === 0) {
        return undefined;
    }
    return services.length <= 2 ? services.join(', ') : `${services.length} services`;
}

/** The set of keys to keep: the top `max` by summed circuits; everything else folds to "Other". */
function topKeys(totals: Map<string, number>, max: number): Set<string> {
    if (totals.size <= max) {
        return new Set(totals.keys());
    }
    const ranked = [...totals.entries()].sort((a, b) => b[1] - a[1]);
    return new Set(ranked.slice(0, max).map(([key]) => key));
}

function nodeId(stage: number, value: string): string {
    return `${stage}:${value}`;
}

function push<K>(map: Map<K, TrafficRow[]>, key: K, flow: TrafficRow): void {
    const bucket = map.get(key);
    if (bucket) {
        bucket.push(flow);
    } else {
        map.set(key, [flow]);
    }
}

/**
 * Build the flow-map data from parsed flows. Flows with no destination address or zero circuits
 * are dropped (nothing to place or no ribbon to draw).
 */
export function buildTrafficSankey(flows: TrafficRow[], options: TrafficSankeyOptions = {}): TrafficSankeyData {
    const expanded = options.expandedIdentities ?? new Set<string>();
    const usable = flows.filter((flow) => addressOf(flow) !== '' && (flow.circuits || 0) > 0);

    // Column totals decide which identities / source addresses / destination addresses are shown
    // individually vs folded. The source-address column is the widest one at scale (one node per device),
    // so it gets its own cap rather than only folding when its parent identity folds.
    const identityTotals = new Map<string, number>();
    const hostIdentityTotals = new Map<string, number>();
    const addressTotals = new Map<string, number>();
    // Distinct real source IPs behind each source identity - the count the expand pill advertises, and what
    // tells us whether an identity has anything to drill into.
    const srcIpsByIdentity = new Map<string, Set<string>>();
    usable.forEach((flow) => {
        const { identity, hostIdentity, srcAddr, address } = keysOf(flow);
        identityTotals.set(identity, (identityTotals.get(identity) ?? 0) + (flow.circuits || 0));
        hostIdentityTotals.set(hostIdentity, (hostIdentityTotals.get(hostIdentity) ?? 0) + (flow.circuits || 0));
        addressTotals.set(address, (addressTotals.get(address) ?? 0) + (flow.circuits || 0));
        if (srcAddr !== NO_SRC_ADDR) {
            const set = srcIpsByIdentity.get(identity) ?? new Set<string>();
            set.add(srcAddr);
            srcIpsByIdentity.set(identity, set);
        }
    });
    const keptIdentities = topKeys(identityTotals, MAX_IDENTITIES);
    const keptHostIdentities = topKeys(hostIdentityTotals, MAX_HOST_IDENTITIES);
    const keptAddresses = topKeys(addressTotals, MAX_ADDRESSES);
    // Source addresses are only shown for expanded, non-folded identities (drill-down), so apply the column
    // cap over just those - the budget is not spent on addresses that will never render.
    const srcAddrTotals = new Map<string, number>();
    usable.forEach((flow) => {
        const { identity, srcAddr } = keysOf(flow);
        if (srcAddr === NO_SRC_ADDR || !keptIdentities.has(identity) || !expanded.has(identity)) return;
        srcAddrTotals.set(srcAddr, (srcAddrTotals.get(srcAddr) ?? 0) + (flow.circuits || 0));
    });
    const keptSrcAddrs = topKeys(srcAddrTotals, MAX_SRC_ADDRESSES);
    const foldedIdentities = new Set<string>();
    const foldedHostIdentities = new Set<string>();
    const foldedSrcAddrs = new Set<string>();
    const foldedAddresses = new Set<string>();

    // Node metadata and per-node / per-link flow indexes, built in one pass.
    const nodeLabel = new Map<string, string>(); // id -> display label
    const nodeKind = new Map<string, string>(); // id -> opaque kind (catch-all)
    const flowsByNode = new Map<string, TrafficRow[]>();
    const flowsByLink = new Map<string, TrafficRow[]>();
    const linkWeight = new Map<string, number>(); // `${src}->${dst}` -> summed circuits
    const linkEnds = new Map<string, { source: string; target: string }>();

    const linkId = (sourceNode: string, targetNode: string) => `${sourceNode}->${targetNode}`;

    usable.forEach((flow) => {
        const keys = keysOf(flow);
        const circuits = flow.circuits || 0;

        // Each column folds its own overflow independently: a source address past the cap drops into
        // "Other sources", a source identity past the cap into "Other identities", a host identity past the
        // cap into "Other host identities", a destination past the cap into "Other destinations" (carrying
        // its endpoints). Ports are never folded.
        const idFolded = !keptIdentities.has(keys.identity);
        // Source addresses are only materialized when their identity is drilled in and the flow carries a
        // real initiator IP; otherwise the chart starts at the source-identity column (gateways-only default).
        const identityExpanded = !idFolded && expanded.has(keys.identity);
        const emitSrcAddr = identityExpanded && keys.srcAddr !== NO_SRC_ADDR;
        const srcFolded = emitSrcAddr && !keptSrcAddrs.has(keys.srcAddr);
        const hostIdFolded = !keptHostIdentities.has(keys.hostIdentity);
        const addrFolded = !keptAddresses.has(keys.address);
        if (srcFolded) foldedSrcAddrs.add(keys.srcAddr);
        if (idFolded) foldedIdentities.add(keys.identity);
        if (hostIdFolded) foldedHostIdentities.add(keys.hostIdentity);
        if (addrFolded) foldedAddresses.add(keys.address);

        const srcAddrVal = srcFolded ? OTHER : keys.srcAddr;
        const idVal = idFolded ? OTHER : keys.identity;
        const hostIdVal = hostIdFolded ? OTHER : keys.hostIdentity;
        // The final node is the specific endpoint (address:port) - the narrow target a service governs.
        // Endpoints of a folded destination address ride along in the shared Other bucket.
        const endpointVal = addrFolded ? OTHER : `${keys.address}:${keys.port}`;

        const srcAddrNode = nodeId(STAGE_SRC_ADDR, srcAddrVal);
        const idNode = nodeId(STAGE_IDENTITY, idVal);
        const hostIdNode = nodeId(STAGE_HOST_IDENTITY, hostIdVal);
        const endpointNode = nodeId(STAGE_ENDPOINT, endpointVal);

        // Labels (the folded/synthetic buckets get their labels finalized after the pass).
        if (emitSrcAddr && !srcFolded) {
            // emitSrcAddr guarantees a real IP, so the label is just the address.
            nodeLabel.set(srcAddrNode, srcAddrVal);
        }
        if (!idFolded) {
            nodeLabel.set(idNode, idVal === NO_IDENTITY ? '(no identity)' : idVal);
        }
        if (!hostIdFolded) {
            nodeLabel.set(hostIdNode, hostIdVal === NO_HOST_IDENTITY ? '(no host identity)' : hostIdVal);
        }
        if (!addrFolded) {
            // The full address:port, since destination and port are now one combined column.
            nodeLabel.set(endpointNode, `${keys.address}:${keys.port}`);
        }

        // Per-node flow index. The source-address node exists only when drilled in.
        if (emitSrcAddr) push(flowsByNode, srcAddrNode, flow);
        push(flowsByNode, idNode, flow);
        push(flowsByNode, hostIdNode, flow);
        push(flowsByNode, endpointNode, flow);

        // Ribbons between adjacent columns: dialer -> binder -> endpoint, plus device -> dialer only when the
        // identity is drilled in. The right side of the graph is identical whether or not expanded.
        const ribbons: [string, string][] = [
            [idNode, hostIdNode],
            [hostIdNode, endpointNode],
        ];
        if (emitSrcAddr) ribbons.unshift([srcAddrNode, idNode]);
        ribbons.forEach(([a, b]) => {
            const id = linkId(a, b);
            linkWeight.set(id, (linkWeight.get(id) ?? 0) + circuits);
            linkEnds.set(id, { source: a, target: b });
            push(flowsByLink, id, flow);
        });
    });

    // Finalize the folded/synthetic bucket labels + mark them catch-all so the CSS mutes them.
    const otherId = nodeId(STAGE_IDENTITY, OTHER);
    const otherHostId = nodeId(STAGE_HOST_IDENTITY, OTHER);
    const otherSrcAddr = nodeId(STAGE_SRC_ADDR, OTHER);
    const otherEndpoint = nodeId(STAGE_ENDPOINT, OTHER);
    if (flowsByNode.has(otherId)) {
        nodeLabel.set(otherId, `Other identities (${foldedIdentities.size})`);
        nodeKind.set(otherId, 'catch-all');
    }
    if (flowsByNode.has(otherHostId)) {
        nodeLabel.set(otherHostId, `Other host identities (${foldedHostIdentities.size})`);
        nodeKind.set(otherHostId, 'catch-all');
    }
    if (flowsByNode.has(otherSrcAddr)) {
        nodeLabel.set(otherSrcAddr, foldedSrcAddrs.size ? `Other sources (${foldedSrcAddrs.size})` : 'Other sources');
        nodeKind.set(otherSrcAddr, 'catch-all');
    }
    if (flowsByNode.has(otherEndpoint)) {
        // The endpoint column now folds whole destinations, so the bucket counts folded destinations.
        nodeLabel.set(otherEndpoint, foldedAddresses.size ? `Other destinations (${foldedAddresses.size})` : 'Other destinations');
        nodeKind.set(otherEndpoint, 'catch-all');
    }

    // Materialize nodes per column.
    const nodes: SankeyChartNode[] = [];
    const labelById = new Map<string, string>();
    flowsByNode.forEach((rows, id) => {
        const stageIndex = Number(id.slice(0, id.indexOf(':')));
        const value = id.slice(id.indexOf(':') + 1);
        const label = nodeLabel.get(id) ?? value;
        const circuits = sumCircuits(rows);
        const kind = nodeKind.get(id);
        labelById.set(id, label);
        // A source identity with real source addresses behind it gets an expand pill to drill into them.
        const ipCount =
            stageIndex === STAGE_IDENTITY && kind !== 'catch-all' ? srcIpsByIdentity.get(value)?.size ?? 0 : 0;
        const expandMeta =
            ipCount > 0 ? { expandable: true, expandCount: ipCount, expanded: expanded.has(value) } : {};
        nodes.push({
            id,
            stageIndex,
            label,
            title: `${label} · ${circuits.toLocaleString()} circuits`,
            kind,
            ariaLabel: `${FLOW_MAP_HEADERS[stageIndex] ?? ''} ${label}, ${circuits} circuits`,
            // A concrete endpoint names the service(s) serving it, shown once under its address.
            ...(stageIndex === STAGE_ENDPOINT ? { serviceLabel: nodeServiceLabel(rows) } : {}),
            ...expandMeta,
        });
    });

    // Materialize ribbons.
    const links: SankeyChartLink[] = [];
    linkWeight.forEach((weight, id) => {
        const ends = linkEnds.get(id);
        if (!ends) return;
        const sourceLbl = labelById.get(ends.source) ?? '';
        const targetLbl = labelById.get(ends.target) ?? '';
        links.push({
            id,
            sourceNodeId: ends.source,
            targetNodeId: ends.target,
            weight,
            ariaLabel: `${sourceLbl} to ${targetLbl}, ${weight} circuits`,
            // The source-to-host ribbon carries the aggregate service label (the single name, or "N
            // services" across the pair). The actual per-endpoint names live under each host address (set
            // as the endpoint node's serviceLabel), so the two read together: the count here, the names there.
            serviceLabel:
                stageOfId(ends.target) === STAGE_HOST_IDENTITY ? linkServiceLabel(flowsByLink.get(id) ?? []) : undefined,
        });
    });

    return { nodes, links, headers: FLOW_MAP_HEADERS, flowsByNode, flowsByLink, labelById };
}

/** The stage a node id belongs to. */
function stageOfId(id: string): number {
    return Number(id.slice(0, id.indexOf(':')));
}

/**
 * Every node and ribbon the given flows traverse, end to end across all columns. Used to highlight the
 * full path a hovered breakdown row represents, from the source address through to its endpoint.
 */
export function highlightForFlows(
    data: TrafficSankeyData,
    flows: TrafficRow[]
): { nodeIds: string[]; linkIds: string[] } {
    const want = new Set(flows);
    const nodeIds: string[] = [];
    data.flowsByNode.forEach((nodeFlows, nodeId) => {
        if (nodeFlows.some((f) => want.has(f))) {
            nodeIds.push(nodeId);
        }
    });
    const linkIds: string[] = [];
    data.flowsByLink.forEach((linkFlows, linkId) => {
        if (linkFlows.some((f) => want.has(f))) {
            linkIds.push(linkId);
        }
    });
    return { nodeIds, linkIds };
}

/** The address:port an endpoint-stage node's flows all share, for its panel title. */
function endpointOf(flows: TrafficRow[], fallback: string): string {
    const set = new Set(
        flows.map((f) => {
            const address = (f.terminator_remote_address ?? '').trim();
            const port = (f.terminator_remote_port ?? '').trim();
            return port ? `${address}:${port}` : address;
        }),
    );
    return set.size === 1 ? [...set][0] : fallback;
}

/** Resolve a node click to its titled panel + underlying flows. */
export function flowsForNode(data: TrafficSankeyData, ev: SankeyNodeClickEvent): ResolvedFlows {
    const label = data.labelById.get(ev.nodeId) ?? '';
    const flows = data.flowsByNode.get(ev.nodeId) ?? [];
    switch (ev.stageIndex) {
        case STAGE_SRC_ADDR:
            // One source address (the originating device, first column): break down the endpoints it reached.
            return { eyebrow: 'Source address', title: label, subject: label, flows, breakdown: 'endpoint', metrics: ['circuits', 'destinations', 'ports'], canCreate: false, mono: true };
        case STAGE_IDENTITY:
            // One source identity (the dialer): lead with the devices behind it, then their destinations
            // and ports.
            return { eyebrow: 'Source identity', title: label, subject: label, flows, breakdown: 'identity', metrics: ['circuits', 'sources', 'destinations'], canCreate: false };
        case STAGE_HOST_IDENTITY:
            // One host identity (the binder): the endpoints it hosts, and how many sources reach them.
            return { eyebrow: 'Host identity', title: label, subject: label, flows, breakdown: 'endpoint', metrics: ['circuits', 'destinations', 'sourceAddresses', 'sourceIdentities'], canCreate: false };
        case STAGE_ENDPOINT: {
            // One specific endpoint (address:port): the narrow thing a service governs. Clicking the node
            // aggregates across every binder that hosts it, so lead with the host identity (one row for the
            // usual single binder, several for HA hosting), then the sources reaching it under each.
            const endpoint = endpointOf(flows, label);
            // Split "address:port" so the header reads the address with a muted ":port" tail (the port is no
            // longer a separate, redundant stat tile). Split on the last colon so an IPv6 address survives.
            const portColon = endpoint.lastIndexOf(':');
            const endpointAddr = portColon > 0 ? endpoint.slice(0, portColon) : endpoint;
            const endpointTail = portColon > 0 ? endpoint.slice(portColon) : '';
            // Circuits is the endpoint's headline total; the dimensions become group-by tiles that pivot the
            // one breakdown table (host identities is the initial grouping - its usual single-binder top row -
            // with source identities, source addresses, and services as the other lenses).
            return { eyebrow: 'Host address', title: endpointAddr, subject: endpoint, flows, breakdown: 'host-source', metrics: ['circuits'], groupBy: ['host-source', 'source', 'source-address', 'service'], canCreate: true, mono: true, titleTail: endpointTail };
        }
        default:
            // Any unforeseen stage: lead with the source addresses behind the selection.
            return { eyebrow: 'Source', title: label, subject: label, flows, breakdown: 'identity', metrics: ['circuits', 'sources', 'destinations'], canCreate: false };
    }
}

/** Resolve a ribbon click to its titled panel + underlying flows. */
export function flowsForLink(data: TrafficSankeyData, ev: SankeyLinkClickEvent): ResolvedFlows {
    const source = data.labelById.get(ev.sourceNodeId) ?? '';
    const target = data.labelById.get(ev.targetNodeId) ?? '';
    const flows = data.flowsByLink.get(ev.linkId) ?? [];
    switch (stageOfId(ev.sourceNodeId)) {
        case STAGE_SRC_ADDR:
            // source address -> source identity: one device's traffic through one dialer; break down where
            // it went.
            return { eyebrow: 'Flow', title: `${source} → ${target}`, subject: source, flows, breakdown: 'endpoint', metrics: ['circuits', 'destinations', 'ports'], canCreate: false };
        case STAGE_IDENTITY:
            // source identity -> host identity: one dialer's traffic through one binder; break down the
            // endpoints it reached.
            return { eyebrow: 'Flow', title: `${source} → ${target}`, subject: source, flows, breakdown: 'endpoint', metrics: ['circuits', 'destinations', 'ports'], canCreate: false };
        case STAGE_HOST_IDENTITY:
        default:
            // host identity -> endpoint: one binder's traffic to one concrete endpoint (address:port); who
            // reaches it, and a service can target it.
            return { eyebrow: 'Host address', title: `${source} → ${target}`, subject: target, flows, breakdown: 'source', metrics: ['circuits', 'sourceAddresses', 'sourceIdentities'], canCreate: true };
    }
}
